import { Button, Icon, Link, Stack, Text } from "@chakra-ui/react"
import { createColumnHelper } from "@tanstack/react-table"
import { ConfirmationDialog } from "components/confirmation/confirmation"
import { ActionDrawer } from "components/drawer"
import LastSeen from "components/status/LastSeen"
import { defaultColumns } from "components/table/defaultColumns"
import { remove } from "lib/firebase"
import { formatAgo } from "lib/presence"
import { MdAirplay, MdFormatListBulleted, MdGroup, MdGroupWork, MdGroups, MdOutlineGroupWork, MdOutlineViewCompactAlt, MdOutlineWebStories, MdPlaylistPlay } from "react-icons/md"
import BroadcastModal from "views/modal/broadcast"
import GroupModal from "views/modal/group"
import LocationModal from "views/modal/location"
import LocationMap from "../components/map"

const helper = createColumnHelper()

export const columnsLocations = [
  ...defaultColumns,
  // Free text of the location; blank locations show "N/A" and sort last
  helper.accessor((row) => row.description || undefined, {
    id: 'description',
    header: 'Description',
    size: 250,
    meta: { searchText: item => item.description || 'N/A' },
    cell: props => <Text noOfLines={2}>{props.getValue() || 'N/A'}</Text>,
  }),
  helper.display({
    id: 'group',
    header: 'Group',
    size: 300,
    meta: { searchText: item => item.group?.name || 'None' },
    cell: props => {
      const data = props.row.original
      if (data.group) {
        return (
          <ActionDrawer
            title='Update group'
            size='2xl'
            trigger={
              <Stack direction='row' spacing={2} align='center'>
                <Icon as={MdOutlineViewCompactAlt} color='green'  />
                <Link color='brand.500'>{data.group.name}</Link>
              </Stack>
            }
            >
            <GroupModal group={data.group} onChange={props.table.options.context?.refresh} />
          </ActionDrawer>
        );
      }
      return 'None'
    },
  }),
  helper.display({
    id: 'broadcast',
    header: 'Broadcast',
    size: 300,
    meta: { searchText: item => (item.broadcast || item.group?.broadcast)?.name || 'None' },
    cell: props => {
      const data = props.row.original
      const broadcast = data.broadcast || data.group?.broadcast || null
      if (broadcast) {
        return (
          <ActionDrawer
            title='Update broadcast'
            size={'xxl'}
            trigger={
              <Stack direction='row' spacing={2} align='center'>
                {broadcast.broadcastList ? (
                  <Icon as={MdOutlineWebStories} color={data.broadcast ? 'brand.500' : 'green'}  />
                ):(
                  <Icon as={MdAirplay} color={data.broadcast ? 'brand.500' : 'green'}   />
                )}
                <Link color='brand.500'>{broadcast.name}</Link>
              </Stack>
            }
            >
            <BroadcastModal broadcast={broadcast} onChange={props.table.options.context?.refresh} />
          </ActionDrawer>
        );
      }
      return 'None'
    },
  }),
  helper.accessor('address', {
    id: 'address',
    header: 'Address',
    size: 300,
    meta: { searchText: item => [item.address || 'No address', item.city].filter(Boolean).join(' ') },
    cell: props => {
      const data = props.row.original
      const label = (
        <Stack direction='column' spacing={0} align='start'>
          <div>{[data.address || 'No address'].join(', ')}</div>
          <div>{[data.city].join(', ')}</div>
        </Stack>
      )
      return (
        <Stack direction='row' spacing={2} align='center'>
          <LocationMap 
            location={data}
            label={label}
          />
        </Stack>
      );
    },
  }),
  helper.accessor('lastPing', {
    id: 'last_ping',
    header: 'Last ping',
    size: 220,
    meta: { searchText: item => formatAgo(item.lastPing) },
    cell: props => <LastSeen value={props.getValue()} />,
  }),
  helper.accessor('lastSync', {
    id: 'last_sync',
    header: 'Last sync',
    size: 220,
    meta: { searchText: item => formatAgo(item.lastSync) },
    // Same warning triangle as the last ping: red when missing or older than 4 hours
    cell: props => <LastSeen value={props.getValue()} />,
  }),
  helper.display({
    id: 'actions',
    header: 'Actions',
    size: 200,
    cell: props => (
      <Stack direction='row' spacing={2} align='center'>
        <ActionDrawer
          title='Update location'
          size='2xl'
          trigger={
            <Button colorScheme='brand' variant='ghost' size='xs'>
              Update
            </Button>
          }
          >
          <LocationModal location={props.row.original} onChange={props.table.options.context?.refresh} />
        </ActionDrawer>
        <ActionDrawer
          title={'Copy location: ' + props.row.original.name}
          size='2xl'
          trigger={
            <Button colorScheme='brand' variant='ghost' size='xs'>
              Copy
            </Button>
          }
          >
          <LocationModal template={props.row.original} onChange={props.table.options.context?.refresh} />
        </ActionDrawer>
        <ConfirmationDialog
          title='Delete location'
          message='Are you sure you want to delete this location?'
          confirmLabel='Delete'
          trigger={
            <Button colorScheme='red' variant='ghost' size='xs'>
              Delete
            </Button>
          }
          onConfirm={async () => {
            await remove('location', props.row.original.id)
            props.table.options.context?.refresh()
          }}
          />
      </Stack>
    ),
  }),
]